/**
 * ServiceRequestForm — iter-249 (Feb 2026)
 * ========================================
 * Creates a new lifecycle service request for a REGISTERED company.
 *
 * Rules:
 *   • Company selection is MANDATORY (auto-complete by trade name / license no).
 *   • Main + Sub service picked from the `company` scope catalog.
 *   • Price + notes + attachments captured up front (invoice is issued
 *     later when the operator moves the request to FEE_COLLECTION).
 *   • On save: the request lands in stage 1 (REQUEST_RECEIVED).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Plus, Loader2, Upload, X, Save, Search,
  Building2, Package, ReceiptText, ClipboardList,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { DirhamPrice } from '../../components/DirhamPrice';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';
import { AgentPageShell } from '../agent/AgentPageShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ServiceRequestForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const preselectedCompany = searchParams.get('company_id') || '';

  const [companies, setCompanies] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loadingBoot, setLoadingBoot] = useState(true);
  const [saving, setSaving] = useState(false);

  const [companyQuery, setCompanyQuery] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [mainId, setMainId] = useState('');
  const [subId, setSubId] = useState('');
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  // ── Boot: load companies + catalog
  useEffect(() => {
    (async () => {
      try {
        const [cRes, catRes] = await Promise.all([
          axios.get(`${API}/agent/companies?limit=500`, { headers: getAuthHeaders() }),
          axios.get(`${API}/admin/service-catalog/company`, { headers: getAuthHeaders() }),
        ]);
        setCompanies(cRes.data?.data || []);
        setCatalog(catRes.data?.data || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || (isAr ? 'فشل التحميل' : 'Load failed'));
      } finally {
        setLoadingBoot(false);
      }
    })();
  }, [isAr]);

  useEffect(() => { if (preselectedCompany) setCompanyId(preselectedCompany); }, [preselectedCompany]);

  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === companyId) || null,
    [companies, companyId],
  );
  const mainService = useMemo(() => catalog.find((m) => m.id === mainId) || null, [catalog, mainId]);
  const subService = useMemo(
    () => (mainService?.sub_services || []).find((s) => s.id === subId) || null,
    [mainService, subId],
  );

  const filteredCompanies = useMemo(() => {
    // iter-262 (Feb 2026) — Only show companies AFTER the operator types at
    // least 1 character. The user complained that all companies were
    // dumped in the list; they want a search-only picker.
    if (!companyQuery.trim()) return [];
    const q = companyQuery.trim().toLowerCase();
    return companies
      .filter((c) => (c.company_name_ar || '').toLowerCase().includes(q)
                  || (c.company_name_en || '').toLowerCase().includes(q)
                  || (c.license_number || '').toLowerCase().includes(q)
                  || (c.id || '').toLowerCase().includes(q))
      .slice(0, 30);
  }, [companies, companyQuery]);

  // ── Upload
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploads = await Promise.all(files.map(async (f) => {
        const form = new FormData();
        form.append('file', f);
        const r = await axios.post(`${API}/upload/file`, form, {
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        });
        return { file_id: r.data.file_id, filename: r.data.filename, size: r.data.size };
      }));
      setAttachments((prev) => [...prev, ...uploads]);
      toast.success(t(`تم رفع ${uploads.length} ملف`, `Uploaded ${uploads.length} file(s)`));
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الرفع' : 'Upload failed'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // ── Submit
  const handleSubmit = async () => {
    if (!selectedCompany) {
      toast.error(t('ملف الشركة إجباري — اختر من القائمة', 'Company selection is required'));
      return;
    }
    if (!mainService || !subService) {
      toast.error(t('اختر الخدمة الرئيسية والفرعية', 'Pick main & sub services'));
      return;
    }
    const priceNum = Number(price);
    if (!(priceNum >= 0) || price === '') {
      toast.error(t('السعر مطلوب', 'Price is required'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        company_id: selectedCompany.id,
        main_service_id: mainService.id,
        main_service_name_ar: mainService.name_ar || '',
        main_service_name_en: mainService.name_en || '',
        sub_service_id: subService.id,
        sub_service_name_ar: subService.name_ar || '',
        sub_service_name_en: subService.name_en || '',
        price: priceNum,
        notes: notes.trim(),
        attachments: attachments.map((a) => ({ file_id: a.file_id, filename: a.filename })),
      };
      const r = await axios.post(`${API}/admin/company-service-requests`, payload, { headers: getAuthHeaders() });
      toast.success(t(
        `تم إنشاء الطلب ${r.data.data.request_no} — المرحلة: تم استلام الطلب`,
        `Request ${r.data.data.request_no} created — Stage: Received`,
      ));
      navigate('/registered-companies-services/tracking');
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل إنشاء الطلب' : 'Failed to create request'));
    } finally {
      setSaving(false);
    }
  };

  const breadcrumb = [
    { label_ar: 'الرئيسية', label_en: 'Home', path: '/dashboard' },
    { label_ar: 'CLOUDGM FZ LLC (للإشراف)', label_en: 'CLOUDGM FZ LLC (Supervision)', path: '/dashboard?platform=fzlc' },
    { label_ar: 'Cloud GM Company Agent', label_en: 'Cloud GM Company Agent', path: '/manage/cloudgm-agent' },
    { label_ar: 'خدمات الشركات المسجلة', label_en: 'Registered Companies Services', path: '/registered-companies-services' },
    { label_ar: 'إدراج خدمة', label_en: 'Insert Service', path: null },
  ];

  if (loadingBoot) {
    return (
      <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate('/registered-companies-services')}>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
        </div>
      </AgentPageShell>
    );
  }

  return (
    <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate('/registered-companies-services')}>
      <div className="max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="h-11 w-11 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <ClipboardList className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white" data-testid="csr-form-title">
                {t('إدراج خدمة لشركة مسجلة', 'Insert Company Service Request')}
              </h1>
              <p className="text-[12px] text-white/50">
                {t(
                  'يدخل الطلب مرحلة "تم استلام الطلب" مباشرة. الفاتورة تُصدر لاحقاً عند مرحلة "تحصيل الرسوم".',
                  'The request enters "Received" immediately. The invoice is issued later at the "Fee Collection" stage.',
                )}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-5">
              {/* Company picker */}
              <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="h-4 w-4 text-emerald-400" />
                  <h2 className="text-sm font-semibold">{t('ملف الشركة *', 'Company File *')}</h2>
                </div>
                {selectedCompany ? (
                  <div className="flex items-center justify-between bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2"
                       data-testid="csr-form-company-chip">
                    <div>
                      <p className="text-sm font-semibold text-white">
                        {isAr ? (selectedCompany.company_name_ar || selectedCompany.company_name_en) : (selectedCompany.company_name_en || selectedCompany.company_name_ar)}
                      </p>
                      <p className="text-[11px] text-white/50">
                        {selectedCompany.license_number || '—'} · {selectedCompany.issuing_authority || '—'}
                      </p>
                    </div>
                    <button
                      onClick={() => { setCompanyId(''); setCompanyQuery(''); }}
                      className="text-white/60 hover:text-white text-[11px]"
                      data-testid="csr-form-company-clear"
                    >
                      {t('تغيير', 'Change')}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="h-4 w-4 text-white/40 absolute top-1/2 -translate-y-1/2 start-3" />
                      <Input
                        value={companyQuery}
                        onChange={(e) => setCompanyQuery(e.target.value)}
                        placeholder={t('ابحث بالاسم أو رقم الرخصة…', 'Search by name or license no…')}
                        className="bg-[#0a0a0a] border-white/10 text-white ps-9"
                        dir={isAr ? 'rtl' : 'ltr'}
                        data-testid="csr-form-company-search"
                      />
                    </div>
                    <ul className="mt-2 max-h-56 overflow-auto space-y-1 pr-1">
                      {/* iter-262 — Empty state differs based on query:
                         • empty query → prompt to type to search
                         • query typed but no matches → "لا توجد نتائج" */}
                      {companyQuery.trim() === '' ? (
                        <li className="text-[12px] text-white/50 py-3 text-center flex items-center justify-center gap-1"
                            data-testid="csr-form-company-empty-prompt">
                          <Search className="h-3 w-3" />
                          {t('اكتب اسم الشركة أو رقم الرخصة للبحث…', 'Start typing a company name or license…')}
                        </li>
                      ) : filteredCompanies.length === 0 ? (
                        <li className="text-[12px] text-white/50 py-2 text-center">
                          {t('لا توجد نتائج', 'No matches')}
                        </li>
                      ) : filteredCompanies.map((c) => (
                        <li key={c.id}>
                          <button
                            onClick={() => setCompanyId(c.id)}
                            className="w-full text-start bg-black/30 hover:bg-white/[0.05] border border-white/5 rounded-lg px-3 py-2"
                            data-testid={`csr-form-company-option-${c.id}`}
                          >
                            <p className="text-[13px] text-white truncate">
                              {isAr ? (c.company_name_ar || c.company_name_en) : (c.company_name_en || c.company_name_ar)}
                            </p>
                            <p className="text-[10px] text-white/40 truncate">
                              {c.license_number || '—'} · {c.issuing_authority || '—'}
                            </p>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </section>

              {/* Service picker */}
              <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Package className="h-4 w-4 text-emerald-400" />
                  <h2 className="text-sm font-semibold">{t('الخدمة *', 'Service *')}</h2>
                </div>
                {catalog.length === 0 ? (
                  <div className="text-[12px] text-white/50">
                    {t(
                      'لا توجد خدمات معرَّفة. أضِف من: التحكم ← منصة التجار ← القوائم المنسدلة ← قوائم خدمات الشركات.',
                      'No services yet. Add from: Control → Merchants → Dropdown Lists → Company Services.',
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <select value={mainId} onChange={(e) => { setMainId(e.target.value); setSubId(''); }}
                            className="w-full h-11 bg-[#0a0a0a] border border-white/10 rounded-md px-3 text-white text-sm"
                            data-testid="csr-form-main-select" dir={isAr ? 'rtl' : 'ltr'}>
                      <option value="">{t('— الخدمة الرئيسية —', '— Main —')}</option>
                      {catalog.map((m) => (
                        <option key={m.id} value={m.id}>{isAr ? m.name_ar : (m.name_en || m.name_ar)}</option>
                      ))}
                    </select>
                    <select value={subId} onChange={(e) => setSubId(e.target.value)} disabled={!mainId}
                            className="w-full h-11 bg-[#0a0a0a] border border-white/10 rounded-md px-3 text-white text-sm disabled:opacity-50"
                            data-testid="csr-form-sub-select" dir={isAr ? 'rtl' : 'ltr'}>
                      <option value="">{mainId ? t('— الخدمة الفرعية —', '— Sub —') : t('اختر الرئيسية أولاً', 'Pick main first')}</option>
                      {(mainService?.sub_services || []).map((s) => (
                        <option key={s.id} value={s.id}>{isAr ? s.name_ar : (s.name_en || s.name_ar)}</option>
                      ))}
                    </select>
                    <Input type="number" min="0" step="0.01" value={price}
                           onChange={(e) => setPrice(e.target.value)}
                           className="bg-[#0a0a0a] border-white/10 text-white h-11"
                           placeholder={t('السعر (د.إ) *', 'Price (AED) *')}
                           data-testid="csr-form-price" />
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                              className="w-full bg-[#0a0a0a] border border-white/10 rounded-md px-3 py-2 text-white text-sm"
                              placeholder={t('ملاحظات (اختياري)', 'Notes (optional)')}
                              dir={isAr ? 'rtl' : 'ltr'} data-testid="csr-form-notes" />
                  </div>
                )}
              </section>

              {/* Attachments */}
              <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Upload className="h-4 w-4 text-cyan-400" />
                  <h2 className="text-sm font-semibold">{t('مرفقات', 'Attachments')}</h2>
                </div>
                <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-cyan-500/40 text-cyan-200 text-[12px] hover:bg-cyan-500/10 cursor-pointer">
                  <Upload className="h-3 w-3" />
                  {uploading ? t('جاري الرفع…', 'Uploading…') : t('رفع ملفات', 'Upload files')}
                  <input type="file" multiple onChange={handleFileChange} className="hidden" disabled={uploading}
                         data-testid="csr-form-upload" />
                </label>
                {attachments.length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {attachments.map((a, i) => (
                      <li key={a.file_id} className="flex items-center gap-2 bg-black/30 border border-white/5 rounded-md px-2 py-1.5 text-[12px]">
                        <ReceiptText className="h-3 w-3 text-white/40" />
                        <span className="flex-1 truncate">{a.filename}</span>
                        <button onClick={() => setAttachments((rows) => rows.filter((_, idx) => idx !== i))}
                                className="h-6 w-6 rounded bg-red-500/10 hover:bg-red-500/20 text-red-400 flex items-center justify-center">
                          <X className="h-3 w-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

            {/* Summary + submit */}
            <div>
              <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 sticky top-6">
                <h2 className="text-sm font-semibold mb-3">{t('ملخص', 'Summary')}</h2>
                <div className="space-y-2 text-[12px] mb-4">
                  <Row label={t('الشركة', 'Company')}
                       value={selectedCompany ? (isAr ? selectedCompany.company_name_ar : selectedCompany.company_name_en) : '—'} />
                  <Row label={t('الرئيسية', 'Main')}
                       value={mainService ? (isAr ? mainService.name_ar : (mainService.name_en || mainService.name_ar)) : '—'} />
                  <Row label={t('الفرعية', 'Sub')}
                       value={subService ? (isAr ? subService.name_ar : (subService.name_en || subService.name_ar)) : '—'} />
                  <div className="flex items-center justify-between pt-2 border-t border-white/5">
                    <span className="text-white/70">{t('السعر', 'Price')}</span>
                    <span className="text-emerald-300 font-bold">
                      <DirhamPrice value={Number(price) || 0} decimals={2} size="sm" />
                    </span>
                  </div>
                </div>
                <Button onClick={handleSubmit} disabled={saving || !selectedCompany || !mainService || !subService}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                        data-testid="csr-form-submit">
                  {saving ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Save className="h-4 w-4 me-2" />}
                  {t('إنشاء الطلب', 'Create Request')}
                </Button>
                <p className="text-[10px] text-white/40 text-center mt-2">
                  {t(
                    'الطلب يدخل المرحلة الأولى بدون فاتورة. الفاتورة CGC تُصدر عند "تحصيل الرسوم".',
                    'Request enters stage 1 without an invoice. CGC invoice issues at "Fee Collection".',
                  )}
                </p>
              </section>
            </div>
          </div>
        </motion.div>
      </div>
    </AgentPageShell>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-white/50">{label}</span>
    <span className="text-white/85 text-end line-clamp-1">{value}</span>
  </div>
);
